/**
 * Question Adapter - Converts between Question Manifest format and ask_user_question format
 *
 * The ask_user_question MCP tool requires:
 *   - question: string (full text)
 *   - header: string (max 12 chars, short label)
 *   - multi_select: boolean
 *   - options: {label, description}[] (REQUIRED, min 2)
 *
 * Our Question Manifest format has:
 *   - id: string (e.g., "api-1")
 *   - question: string
 *   - type: 'text' | 'select' | 'multi-select' | 'confirm'
 *   - options?: {label, value}[]  (optional for text/confirm)
 *   - required?: boolean
 *   - default?: string | boolean | string[]
 *   - dependsOn?: {questionId, value}
 */

export interface QuestionManifestQuestion {
  id: string;
  question: string;
  type: 'text' | 'select' | 'multi-select' | 'confirm';
  required?: boolean;
  options?: { label: string; value: string }[];
  default?: string | boolean | string[];
  help?: string;
  dependsOn?: {
    questionId: string;
    value: string | string[];
  };
}

export interface AskUserQuestionOption {
  label: string;
  description?: string;
}

export interface AskUserQuestion {
  question: string;
  header: string;
  multi_select: boolean;
  options: AskUserQuestionOption[];
}

/**
 * Truncate string to max length, adding ellipsis if needed
 */
function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength - 1) + '…';
}

/**
 * Generate a valid header from a question ID
 * Headers must be max 12 chars, alphanumeric with hyphens
 */
function generateHeader(id: string): string {
  // Remove numbers and prefix, keep meaningful part
  // e.g., "api-1" -> "API-1", "auth-3" -> "AUTH-3"
  const parts = id.split('-');
  if (parts.length >= 2) {
    const prefix = parts[0].toUpperCase().substring(0, 4);
    const num = parts[1];
    return truncate(`${prefix}-${num}`, 12);
  }
  return truncate(
    id
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .substring(0, 12),
    12
  );
}

/**
 * Generate default options for question types that don't have options
 */
function generateDefaultOptions(type: string): AskUserQuestionOption[] {
  switch (type) {
    case 'confirm':
      return [
        { label: 'Yes', description: 'Enable or confirm this option' },
        { label: 'No', description: 'Disable or decline this option' },
      ];
    case 'text':
      return [
        { label: 'Specify', description: 'I will provide a specific value' },
        { label: 'Skip', description: 'Use default or skip this question' },
      ];
    default:
      return [
        { label: 'Option 1', description: 'First option' },
        { label: 'Option 2', description: 'Second option' },
      ];
  }
}

/**
 * Convert a single QuestionManifest question to ask_user_question format
 */
export function adaptQuestion(q: QuestionManifestQuestion): AskUserQuestion {
  // Generate header from ID
  const header = generateHeader(q.id);

  // Determine multi_select
  const multiSelect = q.type === 'multi-select';

  // Handle options - use provided or generate defaults
  let options: AskUserQuestionOption[];

  if (q.options && q.options.length >= 2) {
    // Use provided options, adapt format
    options = q.options.map((opt) => ({
      label: truncate(opt.label, 50),
      description: opt.value !== opt.label ? opt.value : undefined,
    }));
  } else if (q.options && q.options.length === 1) {
    // Single option - add a default
    options = [
      ...q.options.map((opt) => ({ label: truncate(opt.label, 50), description: opt.value })),
      { label: 'Other', description: 'Specify a different value' },
    ];
  } else {
    // No options - generate defaults based on type
    options = generateDefaultOptions(q.type);
  }

  // Ensure minimum 2 options (MCP tool requirement)
  if (options.length < 2) {
    options.push({ label: 'Other', description: 'Alternative option' });
  }

  // Limit to 6 options (MCP tool max)
  options = options.slice(0, 6);

  return {
    question: q.question + (q.help ? `\n\n${q.help}` : ''),
    header,
    multi_select: multiSelect,
    options,
  };
}

/**
 * Adapt multiple questions, filtering by dependsOn
 *
 * @param questions - All questions from manifest
 * @param answers - Previously answered questions (for filtering conditionals)
 * @returns Questions adapted for ask_user_question, with conditionals resolved
 */
export function adaptQuestions(
  questions: QuestionManifestQuestion[],
  answers: Record<string, string | string[] | boolean> = {}
): AskUserQuestion[] {
  const adapted: AskUserQuestion[] = [];

  for (const q of questions) {
    // Check dependsOn condition
    if (q.dependsOn) {
      const dependsAnswer = answers[q.dependsOn.questionId];
      if (dependsAnswer === undefined) {
        // Parent question not answered yet - skip this conditional question
        continue;
      }

      // Check if the answer matches the condition
      const expectedValues = Array.isArray(q.dependsOn.value)
        ? q.dependsOn.value
        : [q.dependsOn.value];

      const answerValue = Array.isArray(dependsAnswer) ? dependsAnswer[0] : dependsAnswer;

      if (!expectedValues.includes(answerValue as string)) {
        // Condition not met - skip this question
        continue;
      }
    }

    adapted.push(adaptQuestion(q));
  }

  return adapted;
}

/**
 * Parse JSON response from LLM (handles various formats)
 */
export function parseLLMQuestionsResponse(text: string): QuestionManifestQuestion[] {
  // Try to extract JSON from the response
  let jsonStr = text;

  // Remove markdown code blocks
  jsonStr = jsonStr.replace(/```(?:json|javascript)?\s*/gi, '');
  jsonStr = jsonStr.replace(/```\s*$/gm, '');

  // Find JSON object or array
  const firstBrace = jsonStr.indexOf('{');
  const firstBracket = jsonStr.indexOf('[');

  let start = -1;
  if (firstBrace !== -1 && firstBracket !== -1) {
    start = Math.min(firstBrace, firstBracket);
  } else if (firstBrace !== -1) {
    start = firstBrace;
  } else if (firstBracket !== -1) {
    start = firstBracket;
  }

  if (start === -1) {
    throw new Error('No JSON found in response');
  }

  jsonStr = jsonStr.substring(start);

  // Handle trailing text after JSON
  const lastBrace = jsonStr.lastIndexOf('}');
  const lastBracket = jsonStr.lastIndexOf(']');
  const end = Math.max(lastBrace, lastBracket);

  if (end === -1) {
    throw new Error('Invalid JSON structure');
  }

  jsonStr = jsonStr.substring(0, end + 1);

  // Fix common issues
  jsonStr = jsonStr.replace(/,(\s*[}\]])/g, '$1'); // Remove trailing commas
  jsonStr = jsonStr.replace(/'/g, '"'); // Single quotes to double

  const parsed = JSON.parse(jsonStr);

  // Handle various JSON structures
  let questions: unknown[];

  if (Array.isArray(parsed)) {
    questions = parsed;
  } else if (parsed.questions && Array.isArray(parsed.questions)) {
    questions = parsed.questions;
  } else if (parsed.data && Array.isArray((parsed.data as Record<string, unknown>).questions)) {
    questions = (parsed.data as Record<string, unknown>).questions as unknown[];
  } else {
    throw new Error('No questions array found in response');
  }

  // Sanitize: strip __proto__, constructor, prototype keys recursively
  // to prevent prototype pollution attacks from malicious LLM output
  function sanitize(obj: Record<string, unknown>): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
        // Skip prototype pollution vectors — they could affect downstream parsing
        continue;
      }
      const val = obj[key];
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        sanitized[key] = sanitize(val as Record<string, unknown>);
      } else if (Array.isArray(val)) {
        sanitized[key] = val.map((item) =>
          item && typeof item === 'object' && !Array.isArray(item)
            ? sanitize(item as Record<string, unknown>)
            : item
        );
      } else {
        sanitized[key] = val;
      }
    }
    return sanitized;
  }

  // Apply sanitization to each question object
  questions = questions.map((q) => {
    if (q && typeof q === 'object') {
      return sanitize(q as Record<string, unknown>);
    }
    return q;
  }) as QuestionManifestQuestion[];

  return questions;
}

/**
 * Convert ask_user_question answers back to manifest format
 */
export function answersToManifestFormat(
  answers: { question_header: string; selected_options: string[]; other_text?: string | null }[],
  questions: QuestionManifestQuestion[]
): Record<string, string | string[] | boolean> {
  const result: Record<string, string | string[] | boolean> = {};

  for (const answer of answers) {
    // Find matching question by header
    const headerLower = answer.question_header.toLowerCase();
    const question = questions.find((q) => {
      const qHeader = generateHeader(q.id).toLowerCase();
      return qHeader === headerLower || q.id.toLowerCase().includes(headerLower);
    });

    if (!question) {
      // Try to match by header prefix
      const matched = questions.find((q) => {
        const qHeader = generateHeader(q.id).toLowerCase();
        return qHeader.startsWith(headerLower.split('-')[0]);
      });
      if (matched) {
        result[matched.id] = answer.selected_options[0] || '';
      }
      continue;
    }

    // Handle multi-select vs single select
    if (question.type === 'multi-select' || answer.selected_options.length > 1) {
      result[question.id] = answer.selected_options;
    } else if (question.type === 'confirm') {
      result[question.id] = answer.selected_options[0] === 'Yes';
    } else {
      // For text or single select, use first option or other_text
      if (answer.other_text) {
        result[question.id] = answer.other_text;
      } else if (answer.selected_options.length > 0) {
        // Map label back to value if possible
        const option = question.options?.find((o) => o.label === answer.selected_options[0]);
        result[question.id] = option?.value ?? answer.selected_options[0];
      }
    }
  }

  return result;
}
